// src/services/calendar.service.ts
import { db } from '../config/firebase';
import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';

// Google Calendar API types
export interface TimeSlot {
  start: string; // ISO datetime
  end: string;   // ISO datetime
  available: boolean;
}

export interface CalendarEvent {
  id?: string;
  summary: string;
  description?: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  attendees?: Array<{
    email: string;
    displayName?: string;
  }>;
  location?: string;
}

export interface AvailabilitySettings {
  timeZone: string;
  workingHours: {
    start: string; // "09:00"
    end: string;   // "17:00"
  };
  workingDays: number[]; // [1,2,3,4,5] for Monday-Friday
  slotDuration: number; // minutes
  bufferTime: number;   // minutes between appointments
  advanceBooking: {
    min: number; // minimum hours in advance
    max: number; // maximum days in advance
  };
}

export interface CalendarConnection {
  provider: 'google' | 'calendly' | 'outlook' | 'icloud';
  connected: boolean;
  email?: string;
  calendarId?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  lastSyncAt?: Timestamp;
  syncDirection?: 'one-way' | 'two-way'; // One-way pushes to calendar, two-way syncs both directions
  eventsToSync?: ('tour' | 'call' | 'meeting')[]; // Which event types to sync
}

export interface UserCalendarSettings {
  userId: string;
  availability: AvailabilitySettings;
  connections: CalendarConnection[];
  defaultProvider?: 'google' | 'calendly' | 'outlook';
  syncEnabled: boolean;
}

// ICS file generation for iCal/Outlook compatibility
export const generateICS = (appointment: {
  id: string;
  title: string;
  description?: string;
  startDate: Date;
  endDate: Date;
  location?: string;
  organizerEmail?: string;
  attendees?: Array<{ email: string; name?: string }>;
}): string => {
  const formatDate = (date: Date): string => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const escapeText = (text: string): string => {
    return text.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');
  };

  let ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Canvassed//Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${appointment.id}@canvassed.com`,
    `DTSTAMP:${formatDate(new Date())}`,
    `DTSTART:${formatDate(appointment.startDate)}`,
    `DTEND:${formatDate(appointment.endDate)}`,
    `SUMMARY:${escapeText(appointment.title)}`
  ];

  if (appointment.description) {
    ics.push(`DESCRIPTION:${escapeText(appointment.description)}`);
  }

  if (appointment.location) {
    ics.push(`LOCATION:${escapeText(appointment.location)}`);
  }

  if (appointment.organizerEmail) {
    ics.push(`ORGANIZER;CN=Canvassed:mailto:${appointment.organizerEmail}`);
  }

  if (appointment.attendees) {
    appointment.attendees.forEach(attendee => {
      const name = attendee.name || attendee.email;
      ics.push(`ATTENDEE;CN=${escapeText(name)}:mailto:${attendee.email}`);
    });
  }

  ics.push('END:VEVENT');
  ics.push('END:VCALENDAR');

  return ics.join('\r\n');
};

export const downloadICS = (appointment: {
  id: string;
  title: string;
  description?: string;
  startDate: Date;
  endDate: Date;
  location?: string;
}): void => {
  const icsContent = generateICS(appointment);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `appointment-${appointment.id}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// OAuth configuration - stored in environment variables
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_REDIRECT_URI = process.env.REACT_APP_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/google/callback`;
const MS_CLIENT_ID = process.env.REACT_APP_MS_CLIENT_ID || '';
const MS_REDIRECT_URI = process.env.REACT_APP_MS_REDIRECT_URI || `${window.location.origin}/auth/microsoft/callback`;

export interface CalendarSyncConfig {
  autoSync: boolean;
  syncInterval: number; // minutes
  eventTypes: ('tour' | 'call' | 'meeting')[];
  reminderMinutes: number[];
  includeLocation: boolean;
  includeParentInfo: boolean;
}

export class CalendarService {
  private static GOOGLE_CLIENT_ID = GOOGLE_CLIENT_ID;
  private static MS_CLIENT_ID = MS_CLIENT_ID;

  static getGoogleAuthUrl(userId: string): string {
    const params = new URLSearchParams({
      client_id: this.GOOGLE_CLIENT_ID,
      redirect_uri: GOOGLE_REDIRECT_URI,
      response_type: 'code',
      scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events',
      access_type: 'offline',
      prompt: 'consent',
      state: userId, // Pass userId to identify user after redirect
    });
    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  static getMicrosoftAuthUrl(userId: string): string {
    const params = new URLSearchParams({
      client_id: this.MS_CLIENT_ID,
      redirect_uri: MS_REDIRECT_URI,
      response_type: 'code',
      scope: 'Calendars.ReadWrite offline_access',
      state: userId,
    });
    return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;
  }

  static async initializeGapi(): Promise<boolean> {
    try {
      if (!this.GOOGLE_CLIENT_ID) {
        console.warn('Google Calendar API: Client ID not configured');
        return false;
      }

      // Load the Google API client library
      // In production, this would initialize gapi.client
      console.log('Google Calendar API: Ready to initialize');
      return true;
    } catch (error) {
      console.error('Failed to initialize Google Calendar API:', error);
      return false;
    }
  }

  static async connectGoogleCalendar(userId: string): Promise<string> {
    if (!this.GOOGLE_CLIENT_ID) {
      throw new Error('Google Calendar is not configured. Please add REACT_APP_GOOGLE_CLIENT_ID to environment.');
    }

    // Save pending connection status
    await this.saveCalendarConnection(userId, {
      provider: 'google',
      connected: false,
      syncDirection: 'one-way',
      eventsToSync: ['tour', 'call', 'meeting'],
      lastSyncAt: Timestamp.now()
    });

    // Return the OAuth URL for the client to redirect to
    return this.getGoogleAuthUrl(userId);
  }

  static async handleGoogleCallback(code: string, userId: string): Promise<boolean> {
    try {
      // In production, exchange code for tokens via backend
      // This is a placeholder - actual implementation requires a backend
      console.log('Google OAuth callback - exchanging code for tokens');

      // Update connection status
      await this.saveCalendarConnection(userId, {
        provider: 'google',
        connected: true,
        accessToken: 'placeholder_token', // Would be real token from OAuth
        lastSyncAt: Timestamp.now()
      });

      return true;
    } catch (error) {
      console.error('Failed to handle Google callback:', error);
      return false;
    }
  }

  static async connectOutlookCalendar(userId: string): Promise<string> {
    if (!this.MS_CLIENT_ID) {
      throw new Error('Outlook Calendar is not configured. Please add REACT_APP_MS_CLIENT_ID to environment.');
    }

    await this.saveCalendarConnection(userId, {
      provider: 'outlook',
      connected: false,
      syncDirection: 'one-way',
      eventsToSync: ['tour', 'call', 'meeting'],
      lastSyncAt: Timestamp.now()
    });

    return this.getMicrosoftAuthUrl(userId);
  }

  static async handleMicrosoftCallback(code: string, userId: string): Promise<boolean> {
    try {
      console.log('Microsoft OAuth callback - exchanging code for tokens');

      await this.saveCalendarConnection(userId, {
        provider: 'outlook',
        connected: true,
        accessToken: 'placeholder_token',
        lastSyncAt: Timestamp.now()
      });

      return true;
    } catch (error) {
      console.error('Failed to handle Microsoft callback:', error);
      return false;
    }
  }

  static async connectICloudCalendar(userId: string, appleId: string, appSpecificPassword: string): Promise<boolean> {
    try {
      // iCloud uses CalDAV protocol with app-specific passwords
      // Store credentials securely (in production, encrypt or use secure storage)
      console.log('Connecting to iCloud Calendar via CalDAV');

      await this.saveCalendarConnection(userId, {
        provider: 'icloud',
        connected: true,
        email: appleId,
        // Note: In production, never store passwords client-side
        // This should go through a secure backend
        syncDirection: 'one-way',
        eventsToSync: ['tour', 'call', 'meeting'],
        lastSyncAt: Timestamp.now()
      });

      return true;
    } catch (error) {
      console.error('Failed to connect iCloud Calendar:', error);
      return false;
    }
  }

  static async syncAppointmentToCalendars(
    userId: string,
    appointment: {
      id: string;
      title: string;
      description?: string;
      startDate: Date;
      endDate: Date;
      location?: string;
      eventType: 'tour' | 'call' | 'meeting';
      attendees?: Array<{ email: string; name?: string }>;
    }
  ): Promise<{ success: boolean; synced: string[] }> {
    const synced: string[] = [];

    try {
      const settings = await this.getUserSettings(userId);
      if (!settings) {
        return { success: true, synced };
      }

      const connectedCalendars = settings.connections.filter(c => c.connected);

      for (const connection of connectedCalendars) {
        // Check if this event type should be synced
        if (connection.eventsToSync && !connection.eventsToSync.includes(appointment.eventType)) {
          continue;
        }

        try {
          switch (connection.provider) {
            case 'google':
              await this.createGoogleEvent(connection, appointment);
              synced.push('google');
              break;
            case 'outlook':
              await this.createOutlookEvent(connection, appointment);
              synced.push('outlook');
              break;
            case 'icloud':
              await this.createICloudEvent(connection, appointment);
              synced.push('icloud');
              break;
          }
        } catch (error) {
          console.error(`Failed to sync to ${connection.provider}:`, error);
        }
      }

      return { success: true, synced };
    } catch (error) {
      console.error('Failed to sync appointment:', error);
      return { success: false, synced };
    }
  }

  private static async createGoogleEvent(
    connection: CalendarConnection,
    appointment: any
  ): Promise<void> {
    // In production, this would use the Google Calendar API
    console.log('Creating Google Calendar event:', appointment.title);
    // Would make API call with connection.accessToken
  }

  private static async createOutlookEvent(
    connection: CalendarConnection,
    appointment: any
  ): Promise<void> {
    // In production, this would use Microsoft Graph API
    console.log('Creating Outlook Calendar event:', appointment.title);
    // Would make API call with connection.accessToken
  }

  private static async createICloudEvent(
    connection: CalendarConnection,
    appointment: any
  ): Promise<void> {
    // In production, this would use CalDAV protocol
    console.log('Creating iCloud Calendar event:', appointment.title);
    // Would make CalDAV request
  }

  static async disconnectCalendar(userId: string, provider: 'google' | 'calendly' | 'outlook'): Promise<void> {
    const settingsRef = doc(db, 'calendarSettings', userId);
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      const data = settingsDoc.data() as UserCalendarSettings;
      const connections = data.connections.filter(c => c.provider !== provider);

      await updateDoc(settingsRef, { connections });
    }
  }

  private static async saveCalendarConnection(userId: string, connection: CalendarConnection): Promise<void> {
    const settingsRef = doc(db, 'calendarSettings', userId);
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      const data = settingsDoc.data() as UserCalendarSettings;
      const existingIndex = data.connections.findIndex(c => c.provider === connection.provider);

      if (existingIndex >= 0) {
        data.connections[existingIndex] = connection;
      } else {
        data.connections.push(connection);
      }

      await updateDoc(settingsRef, { connections: data.connections });
    } else {
      await setDoc(settingsRef, {
        userId,
        availability: await this.getDefaultAvailability(),
        connections: [connection],
        syncEnabled: false
      });
    }
  }

  private static async getDefaultAvailability(): Promise<AvailabilitySettings> {
    return {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      workingHours: {
        start: '09:00',
        end: '17:00'
      },
      workingDays: [1, 2, 3, 4, 5],
      slotDuration: 30,
      bufferTime: 15,
      advanceBooking: {
        min: 2,
        max: 30
      }
    };
  }

  static async getAvailableSlots(
    startDate: Date,
    endDate: Date,
    settings: AvailabilitySettings,
    existingEvents?: CalendarEvent[]
  ): Promise<TimeSlot[]> {
    const slots: TimeSlot[] = [];
    const currentDate = new Date(startDate);
    const now = new Date();
    const minBookingTime = new Date(now.getTime() + settings.advanceBooking.min * 60 * 60 * 1000);

    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();

      if (settings.workingDays.includes(dayOfWeek)) {
        const workStart = new Date(currentDate);
        const [startHour, startMin] = settings.workingHours.start.split(':');
        workStart.setHours(parseInt(startHour), parseInt(startMin), 0, 0);

        const workEnd = new Date(currentDate);
        const [endHour, endMin] = settings.workingHours.end.split(':');
        workEnd.setHours(parseInt(endHour), parseInt(endMin), 0, 0);

        const slotTime = new Date(workStart);
        while (slotTime < workEnd) {
          const slotEnd = new Date(slotTime.getTime() + settings.slotDuration * 60000);

          // Check if slot is in the future (respecting minimum booking time)
          const isInFuture = slotTime > minBookingTime;

          // Check if slot conflicts with existing events
          let isAvailable = isInFuture;
          if (existingEvents && isAvailable) {
            isAvailable = !existingEvents.some(event => {
              const eventStart = new Date(event.start.dateTime);
              const eventEnd = new Date(event.end.dateTime);
              return slotTime < eventEnd && slotEnd > eventStart;
            });
          }

          slots.push({
            start: slotTime.toISOString(),
            end: slotEnd.toISOString(),
            available: isAvailable
          });

          slotTime.setTime(slotTime.getTime() + (settings.slotDuration + settings.bufferTime) * 60000);
        }
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return slots;
  }

  static async createEvent(event: CalendarEvent, userId?: string): Promise<string> {
    // In production, this would sync to connected calendar providers
    console.log('Creating calendar event:', event);

    const eventId = 'event_' + Date.now();

    // If user has connected calendars, sync the event
    if (userId) {
      const settingsRef = doc(db, 'calendarSettings', userId);
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data() as UserCalendarSettings;
        const connectedProviders = data.connections.filter(c => c.connected);

        for (const connection of connectedProviders) {
          console.log(`Would sync event to ${connection.provider}`);
          // In production: call respective API to create event
        }
      }
    }

    return eventId;
  }

  static async getUserSettings(userId: string): Promise<UserCalendarSettings | null> {
    const settingsRef = doc(db, 'calendarSettings', userId);
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      return settingsDoc.data() as UserCalendarSettings;
    }

    return null;
  }

  static async saveAvailabilitySettings(
    userId: string,
    availability: AvailabilitySettings
  ): Promise<void> {
    const settingsRef = doc(db, 'calendarSettings', userId);
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      await updateDoc(settingsRef, { availability });
    } else {
      await setDoc(settingsRef, {
        userId,
        availability,
        connections: [],
        syncEnabled: false
      });
    }
  }

  static async getConnectedCalendars(userId: string): Promise<CalendarConnection[]> {
    const settings = await this.getUserSettings(userId);
    return settings?.connections || [];
  }

  static getCalendarFeedUrl(userId: string): string {
    // In production, this would return a secure URL that generates ICS on demand
    const baseUrl = process.env.REACT_APP_API_URL || 'https://your-app.com';
    return `${baseUrl}/api/calendar/${userId}/feed.ics`;
  }
}

export default CalendarService;
